import { IIngestionAccountsCardDetails } from "interfaces/ingestionAccounts.interface";
import styles from "./IngestionAccountsHeader.module.scss";
import SummaryBox from "@components/SummaryBox/SummaryBox";
import { staticIds } from "@staticIds";
import { staticStrings } from "@staticStrings";
import { useEffect, useState } from "react";
import { RootState } from "@store/configureStore";
import { useDispatch, useSelector } from "react-redux";
import {
  setAccountUsersSelectedCount,
  setCheckedAccountIds,
  setDomainAndUserPresent,
  setIsActive,
  setSelectedPage,
  clearAccountMembersMap
} from "@store/ingestionAccounts/ingestionAccounts.slice";
import { TreeViewDropdown } from "../../common-ui-modules/common-components/dropdown-tree-view/TreeViewDropdown";
import { IFiltersState, IHierarchyTreeData, ITreeNode } from "@interfaces";
import {
  convertDataToFilter,
  convertDataToTree,
  HelperFunctions
} from "../../utils/HelperFunctions";
import {
  setHeirarchyTreeResponseLoader,
  setHierarchyTreeResponse
} from "@store/reporting/account/accountReport.slice";
import { axiosHttp } from "@common-services";
import { PATHS } from "@constants";
import { ALL_OPTIONS } from "../../utils/constants/defaultConstants";
import CustomButton from "../../common-ui-modules/common-components/CustomButton/CustomButton";
import { infoIcon, spritemap } from "@images/index";
import DropDown from "@clayui/drop-down/lib/DropDown";
import Button from "@clayui/button";
import ClayIcon from "@clayui/icon";
import { Tooltip } from "react-tooltip";

interface IngestionAccountsHeaderProps {
  setPayload: (state: IFiltersState[]) => void;
  currPayload: IFiltersState[];
  checkedAccountIds: string[];
  handleBulkActivate: () => void;
  isLoading: boolean;
}

const IngestionAccountsHeader: React.FC<IngestionAccountsHeaderProps> = ({
  setPayload,
  currPayload,
  checkedAccountIds,
  handleBulkActivate,
  isLoading
}) => {
  const [clickedCard, setClickedCard] = useState<number>(0);
  const dispatch = useDispatch();
  const {
    accounts,
    all,
    allAccounts,
    accountLabel,
    accountsLabel,
    searchPlaceHolder
  } = staticStrings.reporting;

  const [prevSelection, setPrevSelection] = useState<IHierarchyTreeData[]>([]);
  const [label, setLabel] = useState(accountsLabel);
  const [domainUserFilter, setDomainUserFilter] = useState<string>("All");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { ingestionAccountsCardContent, accountUsersSelectedCount, isActive } =
    useSelector((state: RootState) => state.ingestionAccounts);

  const domainUserFilterOptions = ["All", "At least 1 user and domain"];

  const handleCardClick = (index: number) => {
    setClickedCard(index);
    dispatch(setSelectedPage(1));
    // Reset checked accounts and count when switching away from inactive tab
    if (index !== 2) {
      dispatch(setCheckedAccountIds([]));
      dispatch(setAccountUsersSelectedCount(0));
      dispatch(clearAccountMembersMap());
    }
    switch (index) {
      case 0:
        return dispatch(setIsActive(""));
      case 1:
        return dispatch(setIsActive(staticStrings.ingestion.accounts.active));
      case 2:
        return dispatch(setIsActive(staticStrings.ingestion.accounts.inactive));
      default:
        return dispatch(setIsActive(""));
    }
  };
  const handleDomainUserFilterChange = (value: string) => {
    setDomainUserFilter(value);
    const isDomainAndUserPresent = value === "At least 1 user and domain";
    dispatch(setDomainAndUserPresent(isDomainAndUserPresent));
    dispatch(setSelectedPage(1));
  };

  const handleCheckBoxSelection = (data: ITreeNode[], count: number) => {
    const payload =
      data.length === 0 || (data.length === 1 && data[0].value === all)
        ? []
        : convertDataToFilter(data);
    if (data.length === 0) {
      setLabel(accountsLabel);
    } else if (data[0] && data[0].value === all) {
      setLabel(allAccounts);
    } else if (count === 1) {
      setLabel(`${count} ${accountLabel}`);
    } else {
      setLabel(`${count} ${accountsLabel}`);
    }
    dispatch(setSelectedPage(1));
    setPayload(payload);
  };
  useEffect(() => {
    dispatch(setHeirarchyTreeResponseLoader(true));
    axiosHttp
      .post(`${PATHS.REPORTING.HIERARCHY_TREE_LIST}`, {
        include_inactive_accounts: true,
        include_accounts_valid_for_ingestion: true
      })
      .then((response) => {
        const result = response.data;
        const treeData = convertDataToTree(result.response.data, []);
        result.response.data = HelperFunctions.sortTreeData([
          { ...ALL_OPTIONS, children: treeData }
        ]);
        dispatch(setHierarchyTreeResponse(result));
        dispatch(setHeirarchyTreeResponseLoader(false));
      })
      .catch((error) => {
        console.error("Error:", error);
        dispatch(setHeirarchyTreeResponseLoader(false));
      });
  }, [dispatch]);

  return (
    <div className={styles.accountCardAndSearchSection}>
      <div className={styles.accountCardOuterContainer}>
        {ingestionAccountsCardContent?.map(
          (data: IIngestionAccountsCardDetails, index: number) => {
            const isActive = clickedCard === index;

            return (
              <div
                className={
                  isActive ? styles.accountCardActive : styles.accountCard
                }
                key={data.id}
                onClick={() => handleCardClick(index)}
              >
                <div className={styles.accountCardExtraPadding}>
                  <SummaryBox
                    headerText={data.headerText}
                    bodyText={data.bodyText}
                    image={data.image}
                    summaryCardId={`${staticIds.ingestion.accounts.card}${index}`}
                  />
                </div>
              </div>
            );
          }
        )}
      </div>
      <div className={styles.searchAccountContainer}>
        {accountUsersSelectedCount > 0 && (
          <div className={styles.dataRefreshDateInfoContainer}>
            <div>
              <img
                src={infoIcon}
                alt="info"
                className={styles.dataRefreshDateInfoContainerImage}
              />
            </div>
            <div className={styles.dateContainer}>
              {accountUsersSelectedCount} Account Users Selected
            </div>
          </div>
        )}
        <div className={styles.treeDropdown}>
          <div className="text">{accounts.treeDropdownlabel}</div>
          <TreeViewDropdown
            showDropdown="always"
            texts={{
              inlineSearchPlaceholder: searchPlaceHolder,
              noMatches: staticStrings.commonTexts.noRecordsFound
            }}
            dropdownLabel={label}
            handleCheckBoxSelection={handleCheckBoxSelection}
            page="ingestionAccounts"
            submitButtonMessage={staticStrings.hierarchyTreeDropdown.done}
            prevSelection={prevSelection}
            setPrevSelection={setPrevSelection}
            dropdownId="account_filter_dropdown"
          />
        </div>
        <div className={styles.domainUserFilterContainer}>
          <DropDown
            active={dropdownOpen}
            onActiveChange={setDropdownOpen}
            trigger={
              <Button
                className={styles.dropDownBtn}
                displayType="unstyled"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                id="domain_user_filter_dropdown"
              >
                <div className={styles.dropDownText}>{domainUserFilter}</div>
                {dropdownOpen ? (
                  <ClayIcon
                    symbol="angle-up"
                    spritemap={spritemap}
                    className={styles.arrowUp}
                  />
                ) : (
                  <ClayIcon
                    symbol="angle-down"
                    spritemap={spritemap}
                    className={styles.arrowDown}
                  />
                )}
              </Button>
            }
          >
            <DropDown.ItemList className={styles.dropDownItemList}>
              {domainUserFilterOptions.map((option, index) => (
                <DropDown.Item
                  key={option}
                  id={`dropdown_item_${index}`}
                  active={option === domainUserFilter}
                  onClick={() => {
                    handleDomainUserFilterChange(option);
                    setDropdownOpen(false);
                  }}
                  data-tooltip-id={`tooltip-domain-filter-${index}`}
                  data-tooltip-content={option}
                >
                  <span className={styles.dropdownItemText}>{option}</span>
                  <Tooltip
                    id={`tooltip-domain-filter-${index}`}
                    place="top"
                    className={styles.tooltip}
                  />
                </DropDown.Item>
              ))}
            </DropDown.ItemList>
          </DropDown>
        </div>
        {isActive === staticStrings.ingestion.accounts.inactive && (
          <CustomButton
            displayType="primary"
            onClick={handleBulkActivate}
            disabled={checkedAccountIds.length === 0 || isLoading}
            id="activate_accounts_button"
            className={styles.activateButton}
          >
            Activate
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default IngestionAccountsHeader;
